/**
 * Manage Portfolio (Phase 4).
 *
 * Authenticated surface for the realtor to publish / unpublish their
 * public profile, copy the public URL, and preview it. Sits at
 * /realtor/portfolio.
 *
 * Publishing requires both verification flags. Backend rejects with
 * 409 when either is missing — we mirror that gating client-side so the
 * realtor sees a clear next step (verification CTA) instead of a hard
 * error.
 */

import {
	CheckCircle2,
	Copy,
	ExternalLink,
	EyeOff,
	Globe,
	RefreshCw,
	ShieldCheck,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	publishMyPortfolio,
	unpublishMyPortfolio,
} from "@/features/realtor/services/public-portfolio-api.service";
import {
	getMyRealtorProfile,
	type RealtorProfile,
} from "@/features/realtor/services/realtor-api.service";
import { StudioButton } from "@/features/studio/theme";

type LoadState =
	| { status: "loading" }
	| { status: "error"; message: string }
	| { status: "loaded"; profile: RealtorProfile };

export function ManagePortfolioPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [load, setLoad] = useState<LoadState>({ status: "loading" });
	const [busy, setBusy] = useState(false);
	// Single unmount flag shared by initial load AND post-publish/
	// unpublish refreshes — without this, a refresh-then-unmount can
	// leak setLoad onto an unmounted component.
	const unmountedRef = useRef(false);

	const refresh = useCallback(async () => {
		setLoad({ status: "loading" });
		const result = await getMyRealtorProfile();
		if (unmountedRef.current) return;
		if (!result.success || !result.profile) {
			setLoad({
				status: "error",
				message:
					result.error ?? t("realtor.portfolio.managePage.profileLoadFailed"),
			});
			return;
		}
		setLoad({ status: "loaded", profile: result.profile });
	}, [t]);

	useEffect(() => {
		unmountedRef.current = false;
		refresh();
		return () => {
			unmountedRef.current = true;
		};
	}, [refresh]);

	const handlePublish = async () => {
		if (load.status !== "loaded") return;
		setBusy(true);
		const result = await publishMyPortfolio();
		if (unmountedRef.current) return;
		setBusy(false);
		if (!result.success) {
			toast.error(
				result.error ?? t("realtor.portfolio.managePage.publishFailed"),
			);
			return;
		}
		toast.success(t("realtor.portfolio.managePage.publishedToast"));
		await refresh();
	};

	const handleUnpublish = async () => {
		// Mirror handlePublish — no-op unless the profile is loaded.
		// Without this guard a click while in error/loading state would
		// fire the request against stale or absent profile context.
		if (load.status !== "loaded") return;
		setBusy(true);
		const result = await unpublishMyPortfolio();
		if (unmountedRef.current) return;
		setBusy(false);
		if (!result.success) {
			toast.error(
				result.error ?? t("realtor.portfolio.managePage.unpublishFailed"),
			);
			return;
		}
		toast.success(t("realtor.portfolio.managePage.unpublishedToast"));
		await refresh();
	};

	const openPreview = (url: string) => {
		// window.open returns null when the popup is blocked; surface
		// that as a toast so the realtor knows to allow popups (or
		// just copy the link).
		const opened = window.open(url, "_blank", "noopener,noreferrer");
		if (!opened) {
			toast.error(t("realtor.portfolio.managePage.previewBlocked"));
		}
	};

	if (load.status === "loading") {
		return (
			<div
				className="bg-dashboard-surface min-h-screen p-6 space-y-8"
				data-testid="manage-portfolio-loading"
			>
				<Skeleton className="h-24 w-full" />
				<Skeleton className="h-32 w-full" />
			</div>
		);
	}

	if (load.status === "error") {
		return (
			<div className="bg-dashboard-surface min-h-screen p-6">
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6 text-center space-y-3" role="alert">
						<p
							className="text-sm"
							style={{ color: "var(--color-dashboard-muted)" }}
						>
							{load.message}
						</p>
						<Button
							variant="outline"
							className="gap-1.5 border-dashboard-border text-dashboard-text bg-dashboard-bg"
							onClick={() => refresh()}
						>
							<RefreshCw className="h-4 w-4" />
							{t("realtor.portfolio.retry")}
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	const { profile } = load;
	const isVerified = profile.reraVerified && profile.idVerified;
	const isPublished = profile.portfolioPublished && Boolean(profile.publicSlug);
	const publicUrl = profile.publicSlug
		? `${window.location.origin}/r/${profile.publicSlug}`
		: null;

	const copyUrl = async () => {
		if (!publicUrl) return;
		try {
			await navigator.clipboard.writeText(publicUrl);
			toast.success(t("realtor.portfolio.managePage.copiedToast"));
		} catch {
			toast.error(t("realtor.portfolio.managePage.copyFailed"));
		}
	};

	return (
		<div className="bg-dashboard-surface min-h-screen p-6 space-y-8">
			<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-6 space-y-2">
					<p
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 11,
							fontWeight: 600,
							letterSpacing: "0.18em",
							textTransform: "uppercase",
							color: "var(--color-dashboard-muted)",
						}}
					>
						Realtor Portal
					</p>
					<h1
						style={{
							fontFamily: "var(--font-display)",
							fontSize: 32,
							fontWeight: 500,
							fontStyle: "italic",
							color: "var(--color-dashboard-text)",
							marginTop: 8,
							lineHeight: 1.1,
						}}
					>
						<span style={{ color: "var(--color-dashboard-accent-display)" }}>
							{t("realtor.portfolio.managePage.title")}
						</span>
					</h1>
					<p
						className="text-sm"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("realtor.portfolio.managePage.subtitle")}
					</p>
				</CardContent>
			</Card>

			{/* Verification gate */}
			{!isVerified && (
				<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-5 flex items-start gap-3">
						<div
							className="rounded-full p-2 shrink-0"
							style={{ backgroundColor: "var(--color-dashboard-bg)" }}
						>
							<ShieldCheck
								className="h-4 w-4"
								style={{ color: "var(--color-dashboard-accent-dark)" }}
							/>
						</div>
						<div className="flex-1 space-y-1">
							<p
								className="text-sm font-semibold"
								style={{ color: "var(--color-dashboard-text)" }}
							>
								{t("realtor.portfolio.managePage.verifyFirstTitle")}
							</p>
							<p
								className="text-xs"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								{t("realtor.portfolio.managePage.verifyFirstBody")}
							</p>
							<StudioButton
								variant="primary"
								size="sm"
								className="mt-2 h-8"
								onClick={() => navigate("/realtor/verification")}
							>
								{t("realtor.portfolio.managePage.startVerification")}
							</StudioButton>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Status card */}
			<Card className="bg-dashboard-bg border-dashboard-border shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-5 space-y-4">
					<div className="flex items-center gap-2">
						{isPublished ? (
							<>
								<div
									className="rounded-full p-1.5"
									style={{
										backgroundColor: "var(--color-dashboard-pill-done-bg)",
									}}
								>
									<Globe
										className="h-4 w-4"
										style={{ color: "var(--color-dashboard-pill-done)" }}
									/>
								</div>
								<span
									className="text-sm font-semibold"
									style={{ color: "var(--color-dashboard-text)" }}
								>
									{t("realtor.portfolio.managePage.statusLive")}
								</span>
							</>
						) : (
							<>
								<div
									className="rounded-full p-1.5"
									style={{ backgroundColor: "var(--color-dashboard-surface)" }}
								>
									<EyeOff
										className="h-4 w-4"
										style={{ color: "var(--color-dashboard-muted)" }}
									/>
								</div>
								<span
									className="text-sm font-semibold"
									style={{ color: "var(--color-dashboard-text)" }}
								>
									{t("realtor.portfolio.managePage.statusOffline")}
								</span>
							</>
						)}
					</div>

					{isPublished && publicUrl ? (
						<div className="space-y-3">
							<div
								className="rounded-md border p-3 text-xs font-mono break-all"
								style={{
									backgroundColor: "var(--color-dashboard-surface)",
									borderColor: "var(--color-dashboard-border)",
									color: "var(--color-dashboard-text)",
								}}
							>
								{publicUrl}
							</div>
							<div className="flex flex-wrap gap-2">
								<Button
									variant="outline"
									size="sm"
									className="gap-1.5 border-dashboard-border text-dashboard-text bg-dashboard-bg"
									onClick={copyUrl}
								>
									<Copy className="h-4 w-4" />
									{t("realtor.portfolio.managePage.copyLink")}
								</Button>
								<Button
									variant="outline"
									size="sm"
									className="gap-1.5 border-dashboard-border text-dashboard-text bg-dashboard-bg"
									onClick={() => openPreview(publicUrl)}
								>
									<ExternalLink className="h-4 w-4" />
									{t("realtor.portfolio.managePage.preview")}
								</Button>
								<Button
									variant="outline"
									size="sm"
									className="gap-1.5 border-dashboard-border bg-dashboard-bg"
									style={{ color: "var(--color-dashboard-pill-risk)" }}
									disabled={busy}
									onClick={handleUnpublish}
								>
									<EyeOff className="h-4 w-4" />
									{t("realtor.portfolio.managePage.unpublish")}
								</Button>
							</div>
						</div>
					) : (
						<div className="space-y-2">
							<p
								className="text-sm"
								style={{ color: "var(--color-dashboard-muted)" }}
							>
								{t("realtor.portfolio.managePage.notPublishedBody")}
							</p>
							<StudioButton
								variant="primary"
								className="gap-1.5"
								disabled={!isVerified || busy}
								onClick={handlePublish}
							>
								<CheckCircle2 className="h-4 w-4" />
								{t("realtor.portfolio.managePage.publish")}
							</StudioButton>
						</div>
					)}
				</CardContent>
			</Card>

			<Button
				variant="outline"
				className="gap-1.5 border-dashboard-border text-dashboard-text bg-dashboard-bg"
				onClick={() => navigate("/realtor/listings")}
			>
				{t("realtor.portfolio.managePage.manageListings")}
			</Button>
		</div>
	);
}
